import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useSubscription, formatINR, usageBand } from "@/lib/subscription";
import { openRazorpayCheckout } from "@/lib/razorpay";
import { toast } from "sonner";
import { Sparkles, MessageCircle, HardDrive, Check, ArrowUpRight, Clock } from "lucide-react";

const BILLING_TOGGLES = [
  { id: "monthly", label: "Monthly" },
  { id: "yearly",  label: "Yearly", badge: "Save 16%" },
];

export default function Subscription() {
  const { sub, loading, refresh } = useSubscription();
  const [plans, setPlans] = useState([]);
  const [cycle, setCycle] = useState("monthly");
  const [invoices, setInvoices] = useState([]);

  useEffect(() => {
    api.get("/subscription/plans").then((r) => setPlans(r.data.plans || [])).catch(() => {});
    api.get("/invoices").then((r) => setInvoices(r.data || [])).catch(() => {});
  }, []);

  const upgrade = async (planId) => {
    try {
      await openRazorpayCheckout({ lookup_key: planId });
      // Razorpay flow redirects on success; when they come back the countdown will refetch.
      refresh();
    } catch (e) { toast.error("Checkout failed"); }
  };

  if (loading || !sub) {
    return <div className="text-sm text-ink-soft">Loading subscription…</div>;
  }

  const isTrial = sub.trial?.is_trial;
  const currentTier = sub.plan?.tier;
  const filteredPlans = plans.filter((p) => p.interval === cycle);

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between">
        <div>
          <span className="overline text-ink-soft">Subscription</span>
          <h1 className="mt-2 font-display text-4xl tracking-tight">Your plan &amp; usage.</h1>
        </div>
      </div>

      {/* Current plan card */}
      <div className="card-widget" data-testid="current-plan-card">
        <div className="flex flex-wrap items-start gap-6 justify-between">
          <div className="min-w-[240px]">
            <div className="overline text-ink-soft">Current plan</div>
            <div className="mt-2 flex items-center gap-3">
              <div className="font-display text-3xl">{sub.plan.name}</div>
              <StatusPill status={sub.status} />
            </div>
            {sub.plan.amount_paise > 0 && (
              <div className="text-sm text-ink-mid mt-1">
                {formatINR(sub.plan.amount_paise)} <span className="text-ink-soft">/ {sub.plan.interval}</span>
              </div>
            )}
            {sub.next_billing_date && (
              <div className="text-xs text-ink-mid mt-2">Next billing: {new Date(sub.next_billing_date).toLocaleDateString("en-IN")}</div>
            )}
          </div>

          {isTrial && (
            <TrialCountdown trial={sub.trial} compact={false} />
          )}
        </div>
      </div>

      {/* Usage bars */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <UsageBar icon={Sparkles}      title="Viyora Copilot"    kind="ai_requests"   used={sub.usage.ai_requests.used}   limit={sub.usage.ai_requests.limit}   suffix="requests" />
        <UsageBar icon={MessageCircle} title="WhatsApp"          kind="whatsapp_msgs" used={sub.usage.whatsapp_msgs.used} limit={sub.usage.whatsapp_msgs.limit} suffix="messages" />
        <UsageBar icon={HardDrive}     title="Storage"           kind="storage_mb"    used={sub.usage.storage_mb.used}    limit={sub.usage.storage_mb.limit}    suffix="MB" />
      </div>
      {sub.usage_period && (
        <p className="text-xs text-ink-soft -mt-3" data-testid="usage-period-note">
          AI &amp; WhatsApp counters reset on the 1st of every month · current period {sub.usage_period}. Storage is cumulative.
        </p>
      )}

      {/* Plans */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="overline text-ink-soft">Available plans</div>
            <h2 className="mt-1 font-display text-2xl">Upgrade or change plan</h2>
          </div>
          <div className="inline-flex rounded-full border border-[#E8EAE9] p-1 bg-white" data-testid="billing-cycle-toggle">
            {BILLING_TOGGLES.map((b) => (
              <button
                key={b.id}
                data-testid={`cycle-${b.id}`}
                onClick={() => setCycle(b.id)}
                className={`px-4 py-1.5 text-xs rounded-full transition ${cycle === b.id ? "bg-pine text-linen" : "text-ink-mid hover:text-ink"}`}
              >
                {b.label}{b.badge && <span className="ml-1 text-[10px] opacity-80">· {b.badge}</span>}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {filteredPlans.map((p) => (
            <PlanCard
              key={p.id}
              plan={p}
              isCurrent={sub.plan.id === p.id}
              currentTier={currentTier}
              onUpgrade={() => upgrade(p.id)}
            />
          ))}
        </div>
      </div>

      {/* Payment history */}
      <div className="card-widget">
        <div className="overline text-ink-soft mb-3">Payment history</div>
        {invoices.length === 0 ? (
          <div className="py-6 text-sm text-ink-soft text-center">No payment records yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-ink-soft overline">
                <th className="py-2">Invoice</th><th>Date</th><th>Status</th><th className="text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {invoices.slice(0, 10).map((i) => (
                <tr key={i.id} className="border-t border-[#E8EAE9]">
                  <td className="py-3 font-mono text-xs">{i.invoice_number || "—"}</td>
                  <td className="text-ink-mid">{new Date(i.created_at).toLocaleDateString("en-IN")}</td>
                  <td>
                    <span className={`text-xs px-2.5 py-1 rounded-full ${i.status === "paid" ? "bg-[#E8F0EB] text-[#2D6A4F]" : "bg-[#FDF3ED] text-[#D06A42]"}`}>{i.status}</span>
                  </td>
                  <td className="text-right font-display">₹{Number(i.total || 0).toLocaleString("en-IN")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
function StatusPill({ status }) {
  const map = {
    trialing: { bg: "#FDF3ED", fg: "#8A3F1E", label: "Trial" },
    active:   { bg: "#E8F0EB", fg: "#2D6A4F", label: "Active" },
    past_due: { bg: "#FCECEC", fg: "#B94A48", label: "Past due" },
    expired:  { bg: "#F1F2F0", fg: "#5C6C64", label: "Expired" },
  };
  const s = map[status] || map.trialing;
  return (
    <span data-testid="plan-status-pill"
      className="inline-block text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full font-medium"
      style={{ background: s.bg, color: s.fg }}>{s.label}</span>
  );
}

// -----------------------------------------------------------------------------
export function TrialCountdown({ trial, compact = true }) {
  if (!trial?.is_trial) return null;
  const { day, total_days, remaining_days } = trial;
  const pct = Math.min(100, Math.round((day / total_days) * 100));
  const urgent = remaining_days <= 5;

  if (compact) {
    return (
      <div data-testid="trial-countdown-pill" className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs border ${urgent ? "bg-[#FCECEC] border-[#EDC7C6] text-[#B94A48]" : "bg-white border-[#E8EAE9] text-ink"}`}>
        <Clock className="w-3.5 h-3.5" />
        Trial · Day {day} of {total_days}
      </div>
    );
  }

  return (
    <div className="min-w-[260px]" data-testid="trial-countdown-block">
      <div className="overline text-ink-soft">Free trial</div>
      <div className={`mt-2 font-display text-2xl ${urgent ? "text-[#B94A48]" : ""}`}>Day {day} of {total_days}</div>
      <div className="mt-3 h-2 bg-linen rounded-full overflow-hidden">
        <div className="h-full bg-pine" style={{ width: `${pct}%`, transition: "width .5s" }} />
      </div>
      <div className="text-xs text-ink-mid mt-2">{remaining_days} days left · all features unlocked</div>
    </div>
  );
}

// -----------------------------------------------------------------------------
function UsageBar({ icon: Icon, title, kind, used, limit, suffix }) {
  const { color, label, pct } = usageBand(used, limit);
  return (
    <div className="card-widget" data-testid={`usage-${kind}`}>
      <div className="flex items-center gap-3">
        <Icon className="w-4 h-4 text-ink" />
        <span className="overline text-ink-soft">{title}</span>
      </div>
      <div className="mt-4 flex items-baseline gap-2">
        <span className="font-display text-3xl">{used.toLocaleString("en-IN")}</span>
        <span className="text-sm text-ink-soft">/ {limit ? limit.toLocaleString("en-IN") : "∞"} {suffix}</span>
      </div>
      <div className="mt-3 h-1.5 bg-linen rounded-full overflow-hidden">
        <div className="h-full transition-all" style={{ width: `${pct}%`, background: color }} />
      </div>
      <div className="mt-1 text-[10px] uppercase tracking-widest" style={{ color }}>{label}</div>
    </div>
  );
}

// -----------------------------------------------------------------------------
function PlanCard({ plan, isCurrent, currentTier, onUpgrade }) {
  const isDowngrade = currentTier === "clinic" && plan.tier === "solo";
  const featured = plan.tier === "clinic";
  const savings = plan.interval === "yearly" ? "Save 16% vs monthly" : null;
  const tagline = plan.tier === "clinic"
    ? "For growing multi-chair practices scaling operations."
    : "For solo dentists running a modern, calm clinic.";
  const highlights = plan.tier === "clinic"
    ? ["Viyora Copilot · 200 requests / month", "WhatsApp automation · 500 messages / month", "Up to 8 chairs · multi-doctor scheduling", "Google Reviews · Inventory · Advanced reports"]
    : ["Everything a solo practice needs", "1 chair · 1 doctor · 1 receptionist", "GST invoicing · online booking · consent + warranty", "Razorpay integrated · WhatsApp reminders"];

  return (
    <div
      data-testid={`plan-card-${plan.id}`}
      className={`relative overflow-hidden rounded-2xl bg-white p-8 transition duration-200 hover:-translate-y-1 flex flex-col ${
        featured
          ? "ring-2 ring-pine shadow-[0_1px_2px_rgba(17,24,39,0.04),0_24px_60px_-28px_rgba(245,83,45,0.35)] hover:shadow-[0_1px_2px_rgba(17,24,39,0.04),0_32px_72px_-24px_rgba(245,83,45,0.45)]"
          : "border border-[#E5E7EB] shadow-[0_1px_2px_rgba(17,24,39,0.04)] hover:shadow-[0_20px_48px_-24px_rgba(17,24,39,0.18)] hover:border-ink-faint"
      }`}
    >
      {/* Header row: plan name + badges */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.14em] font-semibold text-ink-soft">
            {plan.tier === "clinic" ? "Clinic" : "Solo"}
          </div>
          <div className="mt-2 text-2xl font-bold text-ink tracking-tight">
            {plan.name.replace(" (Yearly)", "")}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          {featured && (
            <span className="inline-flex items-center text-[10px] uppercase tracking-[0.14em] px-2.5 py-1 rounded-full bg-pine text-white font-bold shadow-sm">
              Most popular
            </span>
          )}
          {isCurrent && (
            <span className="text-[10px] uppercase tracking-[0.14em] px-2.5 py-1 rounded-full bg-[#E8F0EB] text-[#2D6A4F] font-bold">
              Current
            </span>
          )}
        </div>
      </div>

      <p className="mt-3 text-sm text-ink-mid font-medium leading-relaxed">{tagline}</p>

      {/* Price block */}
      <div className="mt-6 flex items-baseline gap-1.5">
        <span className="text-5xl font-extrabold text-ink tracking-tight tabular-nums">
          {formatINR(plan.amount_paise)}
        </span>
        <span className="text-base text-ink-soft font-medium">
          / {plan.interval === "yearly" ? "year" : "month"}
        </span>
      </div>
      {savings && (
        <div className="mt-3 inline-flex items-center text-xs px-2.5 py-1 rounded-full bg-pine/10 text-pine font-bold tracking-tight">
          {savings}
        </div>
      )}

      {/* Divider */}
      <div className="my-6 h-px bg-[#EEF0F2]" />

      {/* Feature list */}
      <ul className="space-y-3">
        {highlights.map((h, i) => (
          <li key={i} className="flex items-start gap-3 text-sm text-ink-mid font-medium leading-relaxed">
            <span className={`mt-0.5 grid place-items-center w-4 h-4 rounded-full shrink-0 ${featured ? "bg-pine text-white" : "bg-pine/10 text-pine"}`}>
              <Check className="w-2.5 h-2.5" strokeWidth={3.5} />
            </span>
            <span>{h}</span>
          </li>
        ))}
      </ul>

      {/* CTA */}
      <button
        onClick={onUpgrade}
        disabled={isCurrent}
        data-testid={`btn-choose-${plan.id}`}
        className={`w-full mt-8 rounded-full py-3 text-sm font-semibold inline-flex items-center justify-center gap-2 transition ${
          isCurrent
            ? "bg-[#F5F3F1] text-ink-soft cursor-not-allowed"
            : featured
              ? "bg-ink text-white hover:bg-ink-mid shadow-sm"
              : "bg-white border border-ink text-ink hover:bg-ink hover:text-white"
        }`}
      >
        {isCurrent ? "Current plan" : isDowngrade ? "Downgrade" : "Choose plan"}
        {!isCurrent && <ArrowUpRight className="w-4 h-4" />}
      </button>
    </div>
  );
}
